'use client';
import React from 'react';
import * as Sentry from '@sentry/nextjs';
import ErrorFallback from './_ErrorFallback';

type AppError = Error & { digest?: string };

interface Props {
  children: React.ReactNode;
  schema?: string;
  deliveryId?: string;
  /** Additional context shown in the dev error fallback */
  message?: string;
  /** Change this to clear a caught error — e.g. pathname or block id */
  resetKey?: string;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage?: string;
  resetKey?: string;
}

const isProd = process.env.NEXT_PUBLIC_APP_ENV === 'production';

class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, errorMessage: error.message };
  }

  static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (state.hasError && props.resetKey !== state.resetKey) {
      return { hasError: false, errorMessage: undefined, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: AppError, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        cms_schema: this.props.schema ?? 'unknown',
        error_boundary: 'cms_block',
      },
      contexts: {
        cms: { deliveryId: this.props.deliveryId, digest: error.digest },
      },
      extra: { componentStack: info.componentStack },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return isProd ? null : (
      <ErrorFallback
        schema={this.props.schema}
        deliveryId={this.props.deliveryId}
        message={this.props.message}
        errorMessage={this.state.errorMessage}
      />
    );
  }
}

export default ErrorBoundary;